// frontend/src/components/Settings/LaborRates.tsx
import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Input, InputNumber, Radio, Space, Popconfirm, App } from 'antd';
import { laborRateApi } from '../../services/api';
import type { LaborRate } from '../../types';
import { RefreshOpenPrompt } from '../LaborRates/RefreshOpenPrompt';

export function LaborRates() {
  const { message } = App.useApp();
  const [rates, setRates] = useState<LaborRate[]>([]);
  const [newName, setNewName] = useState(''); const [newRate, setNewRate] = useState<number>(150);
  const [prompt, setPrompt] = useState<{ id: number; count: number; rate: number } | null>(null);

  const load = async () => { try { setRates((await laborRateApi.getAll()).data); } catch { message.error('Failed to load rates'); } };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!newName.trim()) return message.error('Name required');
    try { await laborRateApi.create({ name: newName.trim(), hourly_rate: newRate }); setNewName(''); await load(); message.success('Rate added'); }
    catch { message.error('Failed to add rate'); }
  };
  const saveRate = async (r: LaborRate, hourly_rate: number) => {
    try {
      await laborRateApi.update(r.id, { hourly_rate });
      await load();
      const usage = (await laborRateApi.openUsage(r.id)).data;
      if (usage.count > 0) setPrompt({ id: r.id, count: usage.count, rate: hourly_rate });
    } catch { message.error('Failed to update rate'); }
  };
  const makeDefault = async (id: number) => { try { await laborRateApi.setDefault(id); await load(); } catch { message.error('Failed'); } };
  const remove = async (id: number) => {
    try { await laborRateApi.delete(id); await load(); message.success('Deleted'); }
    catch (e: any) { message.error(e?.response?.data?.error || 'Failed to delete'); }
  };

  return (
    <Card title="Labor Rates" size="small">
      <Table rowKey="id" dataSource={rates} pagination={false} size="small"
        columns={[
          { title: 'Name', dataIndex: 'name' },
          { title: 'Rate ($/hr)', dataIndex: 'hourly_rate', render: (v: number, r: LaborRate) => (
            <InputNumber min={0} defaultValue={v} onBlur={(e) => { const nv = Number((e.target as HTMLInputElement).value); if (nv !== v) saveRate(r, nv); }} aria-label={`rate-${r.name}`} />) },
          { title: 'Default', dataIndex: 'is_default', render: (_: any, r: LaborRate) => (
            <Radio checked={r.is_default === 1} onChange={() => makeDefault(r.id)} />) },
          { title: '', render: (_: any, r: LaborRate) => (
            <Popconfirm title="Delete this rate?" onConfirm={() => remove(r.id)}><Button size="small" danger>Delete</Button></Popconfirm>) },
        ]} />
      <Space style={{ marginTop: 12 }}>
        <Input placeholder="Rate name (e.g. Diesel)" value={newName} onChange={e => setNewName(e.target.value)} style={{ width: 180 }} />
        <InputNumber min={0} value={newRate} onChange={v => setNewRate(Number(v) || 0)} aria-label="new-rate" />
        <Button type="primary" onClick={add}>Add rate</Button>
      </Space>
      <RefreshOpenPrompt open={!!prompt} count={prompt?.count || 0} newRate={prompt?.rate || 0}
        onConfirm={async () => { if (prompt) { await laborRateApi.applyToOpen(prompt.id, prompt.rate); message.success('Open estimates updated'); } setPrompt(null); }}
        onSkip={() => setPrompt(null)} />
    </Card>
  );
}
